import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Box, MenuItem, Select } from '@mui/material'
import { GameItem } from '@/service/GameService'
import QuaterService, { QuaterData } from '@/service/QuaterService'
import RightOperationBoard from '@/view/game/RightOperationBoard'


interface OperationTabProps {
    game: GameItem
}

const QUARTERS = [1, 2, 3, 4] as const
const STATUS_ORDER: QuaterData['status'][] = ['출전', '교체', '휴식']

const OperationTab = ({ game }: OperationTabProps) => {
    const isSelfGame = game.type === '자체전'

    const [collapsed, setCollapsed] = useState(false)
    const [selectedQuarter, setSelectedQuarter] = useState<1 | 2 | 3 | 4>(1)
    const [team, setTeam] = useState<'A' | 'B'>('A')
    const [quaters, setQuaters] = useState<QuaterData[]>([])
    const [rightQuaters, setRightQuaters] = useState<QuaterData[]>([])
    const rightRef = useRef<HTMLDivElement>(null)
    const [rightSize, setRightSize] = useState({ width: 0, height: 0 })
    const teamName = localStorage.getItem('TEAM_NAME')

    const POSITIONS = ['CF', 'WF', 'MF', 'CB', 'SB', 'GK'] as const

    const getPositionOrder = (pos?: string) => {
        const idx = POSITIONS.indexOf(pos as any)
        return idx === -1 ? POSITIONS.length : idx
    }

    useEffect(() => {
        if (!game.id) return
        QuaterService.getQuatersByGame(game.id).then(setQuaters)
    }, [game.id])

    /** 플레이어 기준으로 그룹핑 */
    const groupedByPlayer = useMemo(() => {
        const map = new Map<number, QuaterData[]>()

        quaters.forEach(q => {
            if (isSelfGame && q.playerTeamABType !== team) return

            if (!map.has(q.playerId)) {
                map.set(q.playerId, [])
            }
            map.get(q.playerId)!.push(q)
        })

        // 쿼터 번호 정렬
        map.forEach(list =>
            list.sort((a, b) => a.quarterNo - b.quarterNo)
        )

        return Array.from(map.values()).sort((a, b) => {
            const aBase = a[0]
            const bBase = b[0]

            // 1️⃣ 포지션1 기준 정렬
            const posDiff =
                getPositionOrder(aBase.playerPosition1) -
                getPositionOrder(bBase.playerPosition1)

            if (posDiff !== 0) return posDiff

            // 2️⃣ 같은 포지션이면 이름순 (선택)
            return aBase.playerName.localeCompare(bBase.playerName)
        })
    }, [quaters, team, isSelfGame])

    /** 선택된 쿼터만 상태 변경 */
    const toggleStatus = async (q: QuaterData) => {
        const next =
            STATUS_ORDER[(STATUS_ORDER.indexOf(q.status) + 1) % STATUS_ORDER.length]

        const updated = await QuaterService.updateQuater(q.id, {
            status: next,
            pointX: q.pointX,
            pointY: q.pointY
        })

        setQuaters(prev =>
            prev.map(it => (it.id === updated.id ? updated : it))
        )
    }

    const onClickPlayerName = (playerQuaters: QuaterData[]) => {
        const target = playerQuaters.find(
            q => q.quarterNo === selectedQuarter
        )

        if (!target) return

        toggleStatus(target)
    }

    const teamStats = useMemo(() => {
        const filtered = quaters.filter(q =>
            q.quarterNo === selectedQuarter &&
            (!isSelfGame || q.playerTeamABType === team)
        )

        const uniquePlayers = new Set(filtered.map(q => q.playerId)).size

        const playCount = filtered.filter(q => q.status === '출전').length
        const subCount = filtered.filter(q => q.status === '교체').length

        return {
            participants: uniquePlayers,
            playCount,
            subCount
        }
    }, [quaters, selectedQuarter, team, isSelfGame])

    const handleQuaterUpdate = (updated: QuaterData) => {
        setQuaters(prev =>
            prev.map(q => (q.id === updated.id ? updated : q))
        )
    }

    useEffect(() => {
        const filtered = quaters.filter(q =>
            q.quarterNo === selectedQuarter &&
            (q.status === '출전' || q.status === '교체') &&
            (!isSelfGame || q.playerTeamABType === team)
        )

        setRightQuaters(filtered)
    }, [quaters, selectedQuarter, team, isSelfGame])

    useEffect(() => {
        if (!rightRef.current) return

        const observer = new ResizeObserver(entries => {
            const entry = entries[0]
            const { width, height } = entry.contentRect

            setRightSize({
                width: Math.floor(width),
                height: Math.floor(height)
            })
        })

        observer.observe(rightRef.current)

        return () => observer.disconnect()
    }, [])

    return (
        <div className={'quater'}>
            {/* ================= LEFT ================= */}
            <Box
                sx={{
                    width: collapsed ? '10%' : '80%',
                    transition: 'width 0.3s',
                    position: 'relative',
                    borderRight: '1px solid #24cbaa',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {!collapsed && (
                    <>
                        {/* 상단 필터 */}
                        <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
                            <Select
                                size="small"
                                value={selectedQuarter}
                                onChange={e => setSelectedQuarter(e.target.value as any)}
                            >
                                {QUARTERS.map(q => (
                                    <MenuItem key={q} value={q}>{q}Q</MenuItem>
                                ))}
                            </Select>


                                <Select
                                    size="small"
                                    value={team}
                                    onChange={e => setTeam(e.target.value as any)}
                                >
                                    <MenuItem value="A">{isSelfGame?'A Team':teamName}</MenuItem>
                                    {isSelfGame && ( <MenuItem value="B">B Team </MenuItem>)}
                                </Select>

                            <div className={'teamStats-playCount'}>
                                <span>{teamStats.playCount + teamStats.subCount*0.5}</span>
                                <span>/</span>
                                <span>{teamStats.participants}</span>
                            </div>
                        </Box>

                        {/* ================= PLAYER ROW LIST ================= */}
                        <Box className={'quater-list'} >
                            {groupedByPlayer.map(playerQuaters => {
                                const base = playerQuaters[0]

                                // quarterNo → quater 매핑
                                const qMap = new Map(
                                    playerQuaters.map(q => [q.quarterNo, q])
                                )

                                return (
                                    <div
                                        key={base.playerId}
                                        className={'quater-list-item'}
                                    >
                                        {/* 이름 / 포지션 */}
                                        <div
                                            onClick={() => onClickPlayerName(playerQuaters)}
                                            className={'quater-name'}
                                        >
                                            <div className={`quater-name-info ${!base.playerMemberId ? 'mercenary-position' : ''}`} style={{ marginRight: '3px' }}>{base.playerName}</div>
                                            <div className={'position'} style={{ marginRight: '10px'}}>
                                                {base.playerPosition1}
                                                {base.playerPosition2 && ` / ${base.playerPosition2}`}
                                            </div>
                                        </div>

                                        <div className={"quater-item"}>
                                        {/* 1~4Q */}
                                        {[1, 2, 3, 4].map(qNo => {
                                            const q = qMap.get(qNo)
                                            if (!q) return <div key={qNo} />

                                            const isActive = qNo === selectedQuarter

                                            const statusColorMap: Record<QuaterData['status'], string> = {
                                                출전: 'play',
                                                교체: 'sub',
                                                휴식: 'rest'
                                            }

                                            return (
                                                <div
                                                    key={q.id}
                                                    className={`quater-status
                                                                    ${statusColorMap[q.status]}
                                                                    ${!isActive ? 'inactive' : 'active'}
                                                                `}
                                                >
                                                    {isActive ? (
                                                        q.status
                                                    ) : (
                                                        <span className="status-dot">●</span>
                                                        // 또는 👉 <span className="status-dot">🟢</span>
                                                    )}
                                                </div>
                                            )
                                        })}
                                        </div>
                                    </div>
                                )
                            })}
                        </Box>
                    </>
                )}

                {/* 토글 바 */}
                <Box
                    onClick={() => setCollapsed(!collapsed)}
                    className={'collapse-button'}
                >
                    {collapsed ? '▶' : '◀'}
                </Box>
            </Box>

            {/* ================= RIGHT ================= */}
            <div
                ref={rightRef}
                className="quater-right"
                style={{
                    pointerEvents: collapsed ? 'auto' : 'none',
                    opacity: collapsed ? 1 : 0.1,
                    zIndex: collapsed ? 2 : 0,
                    position: 'relative',
                    transition: 'opacity 0.2s ease'
                }}
            >
                {rightSize.width > 0 && rightSize.height > 0 && (
                    <RightOperationBoard
                        quaters={rightQuaters}
                        width={rightSize.width}
                        height={rightSize.height}
                        onQuaterUpdate={handleQuaterUpdate}
                    />
                )}
            </div>


        </div>
    )
}

export default OperationTab
